import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@anyroad/icon';
import { colors } from '@anyroad/theme';
import {
  RescheduleModalContainer,
  PaymentDetailsHeader,
  BackButton,
  DashedDivider,
  Divider,
  PaymentDetailsContainer,
  PaymentDetailsError,
  AmountRow,
  AmountLabelValueRow,
  AmountLabel,
  AmountValueContainer,
  ExpandedPaymentSection,
  ExpandedRow,
  ExpandedLabel,
  SubtotalLine,
  ExpandedValue,
  AmountValue,
  SectionLabel,
  SectionValue,
  RefundTimingNote,
  PriceChangeNote,
  ConfirmButtonRow,
  ConfirmButton,
  ButtonContainerStyle,
} from '../styles';
import {
  formatAmountDueForDisplay,
  parseAmountDue,
  formatCurrencyAmount,
} from '../helpers';
import type { PaymentDetailsProps } from '../types';

export const PaymentDetails = ({
  booking,
  isRefund,
  paymentDetailsResult,
  rescheduleMutation,
  handleConfirmPaymentDetails,
  handleBackToReschedule,
}: PaymentDetailsProps) => {
  const [expanded, setExpanded] = useState(false);
  const { pricing } = paymentDetailsResult;
  const topAmountFormatted = isRefund
    ? formatAmountDueForDisplay(
        pricing.amount_due ?? '',
        true,
        pricing.currency ?? 'USD'
      )
    : formatAmountDueForDisplay(
        pricing.amount_due ?? '',
        false,
        pricing.currency ?? 'USD'
      );
  const bookingTotalFormatted = formatCurrencyAmount(
    pricing.currency ?? 'USD',
    parseAmountDue(pricing.unit_total ?? '') +
      parseAmountDue(pricing.addon_total ?? '')
  );
  return (
    <RescheduleModalContainer data-testid="reschedule-modal-payment-details">
      <PaymentDetailsHeader>
        <BackButton
          data-testid="reschedule-modal-back-button"
          onClick={handleBackToReschedule}
        >
          <Icon name="ThinArrow" fill={colors.text.link} />
        </BackButton>
        <FormattedMessage defaultMessage="Payment Details" />
      </PaymentDetailsHeader>
      <PaymentDetailsContainer>
        {rescheduleMutation.isError ? (
          <PaymentDetailsError>
            <FormattedMessage defaultMessage="An error occurred. Please try again." />
          </PaymentDetailsError>
        ) : (
          <>
            <Divider />
            <AmountRow>
              <PriceChangeNote>
                {isRefund ? (
                  <FormattedMessage defaultMessage="The selected time has a lower price than your original booking" />
                ) : (
                  <FormattedMessage defaultMessage="The selected time has a higher price than your original booking" />
                )}
              </PriceChangeNote>
              <AmountLabelValueRow>
                <AmountLabel>
                  {isRefund ? (
                    <FormattedMessage defaultMessage="Refund Amount" />
                  ) : (
                    <FormattedMessage defaultMessage="Amount due" />
                  )}
                </AmountLabel>
                <AmountValueContainer>
                  <AmountValue isRefund={isRefund}>
                    {topAmountFormatted}
                  </AmountValue>
                  <Icon
                    fill={colors.text.icon}
                    height="14px"
                    name="Chevron"
                    onClick={() => setExpanded(!expanded)}
                    style={{
                      transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                    }}
                    width="14px"
                  />
                </AmountValueContainer>
              </AmountLabelValueRow>
            </AmountRow>
            {expanded && (
              <ExpandedPaymentSection>
                <Divider />
                <ExpandedRow>
                  <div>
                    <ExpandedLabel>
                      <FormattedMessage
                        id="SUBTOTAL"
                        defaultMessage="Subtotal"
                      />
                    </ExpandedLabel>
                    {pricing.units?.length > 0 &&
                      pricing.units.map((u) => (
                        <SubtotalLine key={u.id}>
                          {u.price} x {u.quantity} {u.name}
                        </SubtotalLine>
                      ))}
                  </div>
                  <ExpandedValue>{pricing.unit_total ?? ''}</ExpandedValue>
                </ExpandedRow>
                <Divider />
                <ExpandedRow>
                  <ExpandedLabel>
                    <FormattedMessage defaultMessage="Booking Total" />
                  </ExpandedLabel>
                  <ExpandedValue>{bookingTotalFormatted}</ExpandedValue>
                </ExpandedRow>
                <Divider />
                <ExpandedRow>
                  <ExpandedLabel>
                    <FormattedMessage defaultMessage="Service Fee" />
                  </ExpandedLabel>
                  <ExpandedValue>{pricing.service_fee ?? ''}</ExpandedValue>
                </ExpandedRow>
                <DashedDivider />
                <ExpandedRow>
                  <ExpandedLabel>
                    <FormattedMessage defaultMessage="Amount paid" />
                  </ExpandedLabel>
                  <ExpandedValue>- {pricing.amount_paid ?? ''}</ExpandedValue>
                </ExpandedRow>
              </ExpandedPaymentSection>
            )}
            <Divider />
            <div>
              <SectionLabel>
                <FormattedMessage defaultMessage="Payment Method" />
              </SectionLabel>
              <SectionValue>
                <FormattedMessage
                  defaultMessage="**** **** **** {last4}"
                  values={{
                    last4: booking.cardLast4 ?? '****',
                  }}
                />
              </SectionValue>
              {isRefund && (
                <RefundTimingNote>
                  <FormattedMessage defaultMessage="Refund will appear in your account within 5-7 business days." />
                </RefundTimingNote>
              )}
            </div>
            <Divider />
            {booking.cancellation_policy?.description && (
              <div>
                <SectionLabel>
                  <FormattedMessage defaultMessage="Cancellation Policy" />
                </SectionLabel>
                <SectionValue>
                  {booking.cancellation_policy.description}
                </SectionValue>
              </div>
            )}
            <Divider />
          </>
        )}
        <ConfirmButtonRow>
          <ButtonContainerStyle>
            <ConfirmButton
              data-testid="confirm-payment-button"
              onClick={handleConfirmPaymentDetails}
              disabled={rescheduleMutation.isPending}
              loading={rescheduleMutation.isPending}
            >
              {isRefund ? (
                <FormattedMessage defaultMessage="Confirm Change" />
              ) : (
                <FormattedMessage defaultMessage="Confirm Payment" />
              )}
            </ConfirmButton>
          </ButtonContainerStyle>
        </ConfirmButtonRow>
      </PaymentDetailsContainer>
    </RescheduleModalContainer>
  );
};
